import { useState } from 'react';
import { TimelineDate } from './TimelineDate';
import { HistoryItem } from './HistoryItem';
import pg_icon from '../../../assets/pictures/pg_profile.png';
import { changesMock } from '../../../modules/dashboard/hooks/use-dashboard-mock.hook';
import {
  Change,
  ChangeType,
  RoomChangeType,
} from '../../../modules/dashboard/models/dashboard.types';
import { useMutation } from 'react-query';
import { useFetch } from '../../../shared/hooks/use-fetch.hook';
import { useDashboardHttp } from '../../../modules/dashboard/hooks/use-dashboard-http.hook';
import { Checkbox } from '../../../shared/ui/Checkbox/Checkbox';
type HistoryViewProps = {
  changes: Change[];
  setChanges: React.Dispatch<React.SetStateAction<Change[]>>;
};

export default function HistoryView({ changes, setChanges }: HistoryViewProps) {
  const { handleError, handleRetry } = useFetch();
  const { changeFavoritePost } = useDashboardHttp();
  const [showMessages, setShowMessages] = useState(true);
  const [showUpdates, setShowUpdates] = useState(true);

  const toggleFavoriteMutation = useMutation(
    (id: string) => changeFavoritePost(id),
    {
      retry: (failureCount, error: any) => handleRetry(failureCount, error),
      onSettled(data, error, id) {
        if (data) {
          setChanges((prevChanges) =>
            prevChanges.map((change) =>
              change.id === id ? { ...change, favorite: data.favorite } : change
            )
          );
        }
        if (error) {
          const errRes = error?.response;
          if (errRes) {
            console.error(
              'Failed to add the change to favorites.',
              handleError(errRes.status)
            );
          }
        }
      },
    }
  );

  const visibleChanges = changes.filter((change) => {
    const isMessage = change.room?.change_type === RoomChangeType.NewMessage;

    if (showMessages && showUpdates) {
      return true;
    }
    if (showMessages) {
      return isMessage;
    }
    if (showUpdates) {
      return !isMessage;
    }
    return false;
  });

  // Group all changes by the date when they were created
  const groupedByCreatedAt = visibleChanges.reduce(
    (acc: Record<string, Change[]>, change: Change) => {
      const dateKey = change.create_change_info.created_at.slice(0, 10);

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(change);

      return acc;
    },
    {}
  );

  // Convert the grouped object into an array of [date, changes[]] pairs,
  // and sort them in descending order so that the most recent date comes first
  const sortedEntries = Object.entries(groupedByCreatedAt).sort(
    ([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime()
  );

  const toggleFavorite = (id: string) => {
    toggleFavoriteMutation.mutate(id);
  };

  return (
    <div className="p-4">
      {/* max-w-2xl */}
      <div className="flex gap-4 py-4 border border-x-0 border-solid border-gray-300">
        <div>
          <Checkbox
            content="Nachrichten"
            checkboxColor="#666666"
            color="default"
            checked={showMessages}
            onToggle={(checked) => setShowMessages(checked)}
          />
        </div>
        <div>
          <Checkbox
            content="Updates"
            checkboxColor="#666666"
            color="default"
            checked={showUpdates}
            onToggle={(checked) => setShowUpdates(checked)}
          />
        </div>
      </div>
      {sortedEntries.map(([dateKey, changes]) => {
        const dateObj = new Date(dateKey);
        const formattedDate = dateObj.toLocaleDateString('de-DE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });

        return (
          <div key={dateKey}>
            <TimelineDate date={formattedDate} />
            {changes.map((change: Change) => (
              <HistoryItem
                change={change}
                onToggleFavorite={() => toggleFavorite(change.id)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AvatarGroup({ names }: { names: string[] }) {
  return (
    <div className="flex">
      {/* -space-x-2 */}
      {names.map((name, idx) => (
        <img
          key={idx}
          src={pg_icon}
          className="w-8 h-8 rounded-full border-2 border-white"
          title={name}
          alt={name}
        />
      ))}
    </div>
  );
}
